import { IonButton, IonContent, IonHeader, IonIcon, IonItem, IonList, IonPage, IonRadio, IonRadioGroup, IonText, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { addOutline, removeCircle, closeOutline, checkmarkOutline } from 'ionicons/icons';
import { useIonModal } from '@ionic/react';
import { useState } from 'react';
import SelectRfidModal from '../SelectRfidModal';
import RfidChip from '../../components/RfidChip';
import styles from '../DoorPolicyModal.module.css';
import { modalController } from '@ionic/core';
import { useCommonTranslation, useModalTranslation } from '../../hooks/useTranslation';

export interface EditDoorPolicyRfidPickerProps {
    deviceId: string;
    rfidCodes: string[];
    onSelectRfidCodes: (codes: string[]) => void;
    onClose: () => void;
}

const EditDoorPolicyRfidPicker = ({
    deviceId,
    rfidCodes,
    onSelectRfidCodes,
    onClose,
}: EditDoorPolicyRfidPickerProps) => {
    const [selectedRfidCodes, setSelectedRfidCodes] = useState(rfidCodes);
    const [selectionMode, setSelectionMode] = useState<'any' | 'specific'>(
        rfidCodes.length ? 'specific' : 'any'
    );
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const [presentSelectRfidModal, dismissSelectRfidModal] = useIonModal(SelectRfidModal, {
        dismiss: (rfidCode: string, role: string) => {
            if (role === 'confirm' && rfidCode) {
                const next = Array.from(new Set([...selectedRfidCodes, rfidCode]));
                setSelectedRfidCodes(next);
            }
            dismissSelectRfidModal(rfidCode, role);
        },
        deviceId,
        disabledRfidCodes: selectedRfidCodes,
    });

    const removeRfidCode = (rfidCode: string) => {
        const next = selectedRfidCodes.filter((code) => code !== rfidCode);
        setSelectedRfidCodes(next);
    };

    const addRfidCode = async () => {
        if (selectionMode !== 'specific') {
            return;
        }
        const presentingElement = await modalController.getTop();
        presentSelectRfidModal({
            presentingElement: presentingElement ?? undefined,
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton
                            color="medium"
                            onClick={onClose}
                            aria-label={tCommon('buttons.cancel')}
                        >
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{tModals('door_policy.pickers.rfid.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            color="primary"
                            aria-label={tCommon('buttons.save')}
                            onClick={() => {
                                if (selectionMode === 'any') {
                                    onSelectRfidCodes([]);
                                } else {
                                    onSelectRfidCodes(selectedRfidCodes);
                                }
                                onClose();
                            }}
                        >
                            <IonIcon slot="icon-only" icon={checkmarkOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <section className={`${styles.wrapper} ${styles.section}`}>
                    <h2 className={styles.sectionTitle}>{tModals('door_policy.pickers.rfid.heading')}</h2>
                    <IonList className={styles.listCard}>
                        <IonRadioGroup
                            value={selectionMode}
                            onIonChange={(event) => setSelectionMode(event.detail.value)}
                        >
                            <IonItem>
                                <IonRadio value="any" labelPlacement="end" justify="start">
                                    {tModals('door_policy.pickers.rfid.any_microchip')}
                                </IonRadio>
                            </IonItem>
                            <IonItem>
                                <IonRadio value="specific" labelPlacement="end" justify="start">
                                    {tModals('door_policy.pickers.rfid.specific_microchips')}
                                </IonRadio>
                            </IonItem>
                        </IonRadioGroup>
                        {selectionMode === 'specific' && (
                            <>
                                {selectedRfidCodes.map((rfidCode) => (
                                    <IonItem key={rfidCode} button detail={false} onClick={() => removeRfidCode(rfidCode)}>
                                        <IonIcon slot="start" icon={removeCircle} color="danger" />
                                        <RfidChip rfidCode={rfidCode} deviceId={deviceId} />
                                    </IonItem>
                                ))}
                                <IonItem button detail={false} onClick={addRfidCode}>
                                    <IonIcon slot="start" icon={addOutline} />
                                    <IonText color="primary">{tModals('door_policy.pickers.rfid.add_microchip')}</IonText>
                                </IonItem>
                            </>
                        )}
                    </IonList>
                </section>
            </IonContent>
        </IonPage>
    );
};

export const EditDoorPolicyRfidPickerView = (props: EditDoorPolicyRfidPickerProps) => (
    <EditDoorPolicyRfidPicker {...props} />
);

export default EditDoorPolicyRfidPicker;
